import {useCallback, useMemo} from "react";

export type SortOrder = "asc" | "desc";

export type UseTableQueryArgs<F extends string> = {
    /** Pass through from useQueryModal() */
    searchParams: URLSearchParams;
    /** Pass through from useQueryModal() */
    setParam: (mut: (p: URLSearchParams) => void, opts?: { replace?: boolean }) => void;

    /** Defaults (all optional) */
    defaultPage?: number;        // default 0
    defaultSize?: number;        // default 10
    defaultSortBy: F;            // required: your default sort field
    defaultSortOrder?: SortOrder;// default "desc"

    /** Optional guard: allowed sort fields (silently falls back to default if not allowed) */
    allowedSortFields?: readonly F[];
};

export function useTableQuery<F extends string>({
                                                    searchParams,
                                                    setParam,
                                                    defaultPage = 0,
                                                    defaultSize = 10,
                                                    defaultSortBy,
                                                    defaultSortOrder = "desc",
                                                    allowedSortFields,
                                                }: UseTableQueryArgs<F>) {
    // read
    const page = useMemo(() => Math.max(0, parseInt(searchParams.get("page") ?? `${defaultPage}`, 10) || 0), [searchParams, defaultPage]);
    const size = useMemo(() => Math.max(1, parseInt(searchParams.get("size") ?? `${defaultSize}`, 10) || defaultSize), [searchParams, defaultSize]);

    const rawSortBy = (searchParams.get("sortBy") as F | null) ?? defaultSortBy;
    const sortBy = useMemo<F>(() => {
        if (!allowedSortFields) return rawSortBy;
        return (allowedSortFields as readonly string[]).includes(rawSortBy) ? rawSortBy : defaultSortBy;
    }, [rawSortBy, allowedSortFields, defaultSortBy]) as F;

    const sortOrder = ((searchParams.get("sortOrder") as SortOrder | null) ?? defaultSortOrder) as SortOrder;

    // writers
    const setPage = useCallback((next: number) => {
        setParam((p) => {
            p.set("page", String(Math.max(0, next)));
            p.set("size", String(size));
            p.set("sortBy", sortBy);
            p.set("sortOrder", sortOrder);
        });
    }, [setParam, size, sortBy, sortOrder]);

    const setSize = useCallback((next: number) => {
        setParam((p) => {
            p.set("page", "0"); // reset to first page when page-size changes
            p.set("size", String(Math.max(1, next)));
            p.set("sortBy", sortBy);
            p.set("sortOrder", sortOrder);
        });
    }, [setParam, sortBy, sortOrder]);

    const onRequestSort = useCallback((field: F) => {
        const nextOrder: SortOrder = field === sortBy ? (sortOrder === "asc" ? "desc" : "asc") : "desc";
        setParam((p) => {
            p.set("sortBy", field);
            p.set("sortOrder", nextOrder);
            p.set("page", "0");
            p.set("size", String(size));
        });
    }, [setParam, sortBy, sortOrder, size]);

    const setSort = useCallback((field: F, order: SortOrder) => {
        setParam((p) => {
            p.set("sortBy", field);
            p.set("sortOrder", order);
            p.set("page", "0");
            p.set("size", String(size));
        });
    }, [setParam, size]);

    return {
        // state
        page,
        size,
        sortBy,
        sortOrder,
        // actions
        setPage,
        setSize,
        onRequestSort,
        setSort,
    };
}
